import React from 'react';

import { Card, Group, Stack, Text, Title, UnstyledButton, createStyles, rem, px } from '@mantine/core';
import { IconAlarm, IconCalendarTime, IconCoins } from '@tabler/icons-react';
import { TOOLTIP_MESSAGES } from 'components/KiloTooltip';
import useFlag from 'hooks/flags/useFlag';

import { PackageType } from '../../types';

const useStyles = createStyles(theme => ({
  card: {
    border: `${rem(2)} solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.lg,
    padding: theme.spacing.xl,
    cursor: 'pointer',
    transition: 'all 150ms ease',
    minHeight: px(102),
    maxWidth: '440px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',

    '&:hover': {
      borderColor: theme.colors.blue[2],
      backgroundColor: theme.colors.blue[0],
    },

    '&[data-selected]': {
      borderColor: theme.colors.blue[2],
      backgroundColor: theme.colors.blue[0],
    },
  },

  cardContent: {
    textAlign: 'center',
  },

  icon: {
    color: theme.colors.gray[7],
    marginBottom: theme.spacing.md,

    '&[data-selected]': {
      color: theme.colors.blue[6],
    },
  },
}));

interface IPackageTypeSelectionProps {
  selectedType: PackageType | null;
  onSelect: (type: PackageType) => void;
}

const PackageTypeSelection = ({ selectedType, onSelect }: IPackageTypeSelectionProps): JSX.Element => {
  const { classes } = useStyles();
  // TODO(TIME_OR_CREDIT_PACKAGE_RELEASE): Remove after full release
  const { data: timeOrCreditPackageFlag } = useFlag('time_or_credit_package');
  const timeOrCreditPackageEnabled = timeOrCreditPackageFlag?.data?.isActive === true;

  const allPackageTypes = [
    {
      type: 'TIME_BASED' as PackageType,
      title: 'Time-based Package',
      description: TOOLTIP_MESSAGES.PACKAGE_TYPE_TIME_BASED,
      example: 'e.g. Renews every month, even if all sessions weren’t used.',
      icon: <IconCalendarTime size={52} className={classes.icon} />,
    },
    {
      type: 'CREDIT_BASED' as PackageType,
      title: 'Credit-based Package',
      description: TOOLTIP_MESSAGES.PACKAGE_TYPE_CREDIT_BASED,
      example: 'e.g. Renews after 10 sessions are used independently of the time that has passed. ',
      icon: <IconCoins size={52} className={classes.icon} />,
    },
    {
      type: 'TIME_OR_CREDIT_BASED' as PackageType,
      title: 'Time or Credit-based Package',
      description: TOOLTIP_MESSAGES.PACKAGE_TYPE_TIME_OR_CREDIT_BASED,
      example: 'e.g. A 30-day, 12-session package renews on day 30, or as soon as the 12th session is used.',
      icon: <IconAlarm size={52} className={classes.icon} />,
    },
  ];
  const packageTypes = allPackageTypes.filter(
    packageType => packageType.type !== 'TIME_OR_CREDIT_BASED' || timeOrCreditPackageEnabled,
  );

  return (
    <Stack spacing='xl' style={{ padding: '2rem', justifyContent: 'center' }}>
      <Stack spacing='xl' sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Group
          ta='left'
          sx={{
            maxWidth: '430px',
            width: '100%',
          }}
        >
          <Title order={3} weight={500} mb='sm'>
            Select Package Type
          </Title>
        </Group>

        {packageTypes.map(packageType => (
          <UnstyledButton
            key={packageType.type}
            onClick={() => onSelect(packageType.type)}
            data-cy={`packageTypeCard-${packageType.type}`}
          >
            <Card
              className={classes.card}
              data-selected={selectedType === packageType.type ? true : undefined}
            >
              <Group noWrap>
                {packageType.icon}

                <Stack spacing='2px'>
                  <Text size='lg' color='gray.7' fw={600}>
                    {packageType.title}
                  </Text>
                  <Text color='gray.7'>{packageType.description}</Text>
                  <Text color='gray.6'>{packageType.example}</Text>
                </Stack>
              </Group>
            </Card>
          </UnstyledButton>
        ))}
      </Stack>
    </Stack>
  );
};

export default PackageTypeSelection;
